import React, { useState, useEffect } from 'react';
import { X, Camera, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { condoApi, Pet } from '../services/api';
import { useCustomer } from '../contexts/CustomerContext';
import UnitSelector from './UnitSelector';
import PhotoUploadModal, { PhotoResult } from './PhotoUploadModal';
import { PET_SPECIES_LABELS } from '../utils/petLabels';

interface PetFormModalProps {
  customerId: string;
  /** Set when the modal is opened from a unit's page — the pet belongs to that unit. */
  unitId?: string;
  /** Display label for `unitId`; shown read-only in place of the selector. */
  unitName?: string;
  pet?: Pet | null;
  onSuccess: () => void;
  onClose: () => void;
  lightTheme?: boolean;
}

type UnitOption = { value: string; label: string };

const SPECIES_OPTIONS = ['Cachorro', 'Gato', 'Ave', 'Réptil', 'Roedor', 'Peixe', 'Outro'];
const SIZE_OPTIONS = ['Miniatura', 'Pequeno', 'Médio', 'Grande', 'Gigante'];
const SEX_OPTIONS = ['Macho', 'Fêmea'];

// Legacy pets were seeded with raw English enum values — normalize them to the Portuguese
// option labels above so editing an old pet doesn't show an empty/unmatched select.
const SEX_LABELS: Record<string, string> = { male: 'Macho', female: 'Fêmea' };
const SIZE_LABELS: Record<string, string> = { mini: 'Miniatura', small: 'Pequeno', medium: 'Médio', large: 'Grande', giant: 'Gigante' };

const normalizePetField = (value: string | null | undefined, map: Record<string, string>): string =>
  value ? (map[value.toLowerCase()] ?? value) : '';

const PetFormModal: React.FC<PetFormModalProps> = ({ customerId, unitId, unitName, pet, onSuccess, onClose, lightTheme = false }) => {
  // Theme-aware classes — mirrors PetDetailModal's pattern so edit/view actually match and
  // this modal isn't stuck rendering dark chrome (unreadable placeholders) inside a light app.
  const surface = lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700';
  const headerDivider = lightTheme ? 'border-gray-200' : 'border-gray-700';
  const divider = lightTheme ? 'border-gray-100' : 'border-gray-700/60';
  const labelCls = `block text-[10px] font-semibold uppercase tracking-widest mb-1.5 ${lightTheme ? 'text-gray-500' : 'text-gray-500'}`;
  const optionalCls = lightTheme ? 'font-normal text-gray-400 normal-case' : 'font-normal text-gray-600 normal-case';
  const inputCls = `w-full px-3 py-2.5 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 ${
    lightTheme
      ? 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
      : 'bg-gray-800 border border-gray-700 text-white placeholder-gray-600'
  }`;
  const selectBtnCls = (active: boolean) =>
    `flex-1 py-2 text-xs font-medium rounded-lg border transition-colors ${
      active
        ? 'bg-amber-500 border-amber-500 text-white'
        : lightTheme
          ? 'bg-gray-100 border-gray-300 text-gray-600 hover:border-gray-400'
          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
    }`;
  const photoPlaceholderCls = lightTheme
    ? 'bg-gray-100 text-gray-400 group-hover:text-gray-500'
    : 'bg-gray-800 text-gray-600 group-hover:text-gray-400';
  const photoSlotPlaceholderCls = lightTheme
    ? 'bg-gray-100 text-gray-400 group-hover:text-amber-600'
    : 'bg-gray-800/60 text-gray-600 group-hover:text-amber-500';
  const { selectedCustomer } = useCustomer();
  const [name, setName] = useState(pet?.name || '');
  const [breed, setBreed] = useState(pet?.breed || '');
  const [description, setDescription] = useState(pet?.description || '');
  const [species, setSpecies] = useState(normalizePetField(pet?.species, PET_SPECIES_LABELS));
  const [size, setSize] = useState(normalizePetField(pet?.size, SIZE_LABELS));
  const [weight, setWeight] = useState(pet?.weight != null ? String(pet.weight) : '');
  const [sex, setSex] = useState(normalizePetField(pet?.sex, SEX_LABELS));
  const [color, setColor] = useState(pet?.color || '');
  const [castrated, setCastrated] = useState<boolean | null>(pet?.castrated ?? null);
  const [vaccines, setVaccines] = useState(pet?.vaccines || '');
  const [saving, setSaving] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [activeSlot, setActiveSlot] = useState<1 | 2 | 3 | 4>(1);

  // Four photo slots
  const [photoFiles, setPhotoFiles] = useState<(File | null)[]>([null, null, null, null]);
  const [photoPreviews, setPhotoPreviews] = useState<(string | null)[]>([
    pet?.photo_url || null,
    pet?.photo_url_2 || null,
    pet?.photo_url_3 || null,
    pet?.photo_url_4 || null,
  ]);

  // Unit — single async searchable select
  const [selectedUnit, setSelectedUnit] = useState<UnitOption | null>(() => {
    if (pet?.unit) {
      const label = pet.unit.block
        ? `${pet.unit.block.name} - ${pet.unit.number}`
        : pet.unit.number;
      return { value: pet.unit_id || '', label };
    }
    return null;
  });

  useEffect(() => {
    if (pet) {
      setName(pet.name);
      setBreed(pet.breed || '');
      setDescription(pet.description || '');
      setSpecies(normalizePetField(pet.species, PET_SPECIES_LABELS));
      setSize(normalizePetField(pet.size, SIZE_LABELS));
      setWeight(pet.weight != null ? String(pet.weight) : '');
      setSex(normalizePetField(pet.sex, SEX_LABELS));
      setColor(pet.color || '');
      setCastrated(pet.castrated ?? null);
      setVaccines(pet.vaccines || '');
      setPhotoPreviews([pet.photo_url || null, pet.photo_url_2 || null, pet.photo_url_3 || null, pet.photo_url_4 || null]);
      if (pet.unit) {
        const parts: string[] = [];
        if (pet.unit.block?.name) parts.push(pet.unit.block.name);
        if ((pet.unit as any).street?.name) {
          const streetPart = (pet.unit as any).street_number
            ? `${(pet.unit as any).street.name}, ${(pet.unit as any).street_number}`
            : (pet.unit as any).street.name;
          parts.push(streetPart);
        }
        parts.push(pet.unit.number);
        setSelectedUnit({ value: pet.unit_id || '', label: parts.join(' – ') });
      }
    }
  }, [pet]);

  const handlePhotoClick = (slot: 1 | 2 | 3 | 4) => {
    setActiveSlot(slot);
    setShowPhotoModal(true);
  };

  const handlePhotoCropped = (result: PhotoResult) => {
    const file = new File([result.blob], 'photo.jpg', { type: result.blob.type || 'image/jpeg' });
    const newFiles = [...photoFiles];
    newFiles[activeSlot - 1] = file;
    setPhotoFiles(newFiles);
    const newPreviews = [...photoPreviews];
    newPreviews[activeSlot - 1] = result.base64;
    setPhotoPreviews(newPreviews);
    setShowPhotoModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Nome é obrigatório'); return; }

    setSaving(true);
    try {
      let petId: string;
      const parsedWeight = weight.trim() ? parseFloat(weight.replace(',', '.')) : undefined;
      const payload = {
        name: name.trim(),
        breed: breed.trim() || undefined,
        description: description.trim() || undefined,
        unit_id: selectedUnit?.value || unitId || undefined,
        species: species.trim() || undefined,
        size: size || undefined,
        weight: parsedWeight && !isNaN(parsedWeight) ? parsedWeight : undefined,
        sex: sex || undefined,
        color: color.trim() || undefined,
        castrated: castrated !== null ? castrated : undefined,
        vaccines: vaccines.trim() || undefined,
      };

      if (pet) {
        const res = await condoApi.updatePet(customerId, pet.id, payload);
        petId = res.pet?.id || pet.id;
      } else {
        const res = await condoApi.createPet(customerId, payload);
        petId = res.pet?.id;
      }

      for (let i = 0; i < 4; i++) {
        if (photoFiles[i] && petId) {
          try { await condoApi.uploadPetPhoto(customerId, petId, photoFiles[i]!, i + 1); } catch {}
        }
      }

      toast.success(pet ? 'Pet atualizado!' : 'Pet cadastrado!');
      onSuccess();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Erro ao salvar pet');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60" onClick={onClose} />
        <div className={`relative z-10 w-full max-w-md rounded-xl shadow-2xl border flex flex-col max-h-[90vh] ${surface}`}>

          {/* Header */}
          <div className={`flex items-center justify-between px-5 py-4 border-b flex-shrink-0 ${headerDivider}`}>
            <h2 className={`text-xs font-bold uppercase tracking-widest ${lightTheme ? 'text-gray-500' : 'text-gray-400'}`}>
              {pet ? 'EDITAR PET' : 'CADASTRAR PET'}
            </h2>
            <button onClick={onClose} className={`transition-colors ${lightTheme ? 'text-gray-400 hover:text-gray-600' : 'text-gray-400 hover:text-white'}`}>
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col overflow-y-auto">
            {/* Top section — photos in a big left column, name field on the right. Same
                layout/sizes as PetDetailModal's "Detalhes do Pet" so edit and view match. */}
            <div className={`flex items-center gap-5 p-5 flex-shrink-0 border-b ${divider}`}>
              {/* Photo column */}
              <div className="flex gap-2 flex-shrink-0">
                {/* Slot 1 — main */}
                <button
                  type="button"
                  onClick={() => handlePhotoClick(1)}
                  className="relative group w-40 h-[200px] flex-shrink-0 rounded-lg overflow-hidden"
                >
                  {photoPreviews[0] ? (
                    <img src={photoPreviews[0]} alt="Principal" className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full flex flex-col items-center justify-center gap-2 transition-colors ${photoPlaceholderCls}`}>
                      <Camera className="h-7 w-7" />
                      <span className="text-[10px] font-medium uppercase tracking-wider">Principal</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-white rounded-full p-2.5 shadow-xl">
                      <Camera className="h-5 w-5 text-gray-700" />
                    </div>
                  </div>
                </button>

                {/* Slots 2, 3 & 4 stacked — same 4:5 ratio, scaled down */}
                <div className="flex flex-col gap-2">
                  {([2, 3, 4] as const).map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => handlePhotoClick(slot)}
                      className="relative group w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden"
                    >
                      {photoPreviews[slot - 1] ? (
                        <img src={photoPreviews[slot - 1]!} alt={`Foto ${slot}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full flex flex-col items-center justify-center gap-1 transition-colors ${photoSlotPlaceholderCls}`}>
                          <Camera className="h-4 w-4" />
                          <span className="text-[8px] font-medium uppercase tracking-wider">Foto {slot}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-white rounded-full p-1.5 shadow-xl">
                          <Camera className="h-3.5 w-3.5 text-gray-700" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Name — fills the rest of the row instead of sitting in its own full-width
                  block below (which left the photo row with dead space). */}
              <div className="flex-1 min-w-0 self-center">
                <label className={labelCls}>Nome <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputCls}
                  placeholder="Ex: Rex, Bolinha..."
                  maxLength={100}
                  autoFocus
                />
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Espécie + Raça */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Espécie</label>
                  <select
                    value={species}
                    onChange={(e) => setSpecies(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Selecionar...</option>
                    {SPECIES_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Raça <span className={optionalCls}>(opcional)</span></label>
                  <input
                    type="text"
                    value={breed}
                    onChange={(e) => setBreed(e.target.value)}
                    className={inputCls}
                    placeholder="Ex: Labrador..."
                    maxLength={100}
                  />
                </div>
              </div>

              {/* Sexo */}
              <div>
                <label className={labelCls}>Sexo</label>
                <div className="flex gap-2">
                  {SEX_OPTIONS.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSex(sex === s ? '' : s)}
                      className={selectBtnCls(sex === s)}
                    >
                      {s}
                    </button>
                  ))}
                  {sex && (
                    <button
                      type="button"
                      onClick={() => setSex('')}
                      className={`px-2 py-2 text-xs transition-colors ${lightTheme ? 'text-gray-400 hover:text-gray-600' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Porte + Peso */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Porte</label>
                  <select
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Selecionar...</option>
                    {SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Peso (kg)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className={inputCls}
                    placeholder="Ex: 4.5"
                    maxLength={8}
                  />
                </div>
              </div>

              {/* Cor + Castrado */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Cor</label>
                  <input
                    type="text"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className={inputCls}
                    placeholder="Ex: Caramelo..."
                    maxLength={80}
                  />
                </div>
                <div>
                  <label className={labelCls}>Castrado?</label>
                  <div className="flex gap-2">
                    {[true, false].map(val => (
                      <button
                        key={String(val)}
                        type="button"
                        onClick={() => setCastrated(castrated === val ? null : val)}
                        className={selectBtnCls(castrated === val)}
                      >
                        {val ? 'Sim' : 'Não'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vacinas */}
              <div>
                <label className={labelCls}>Vacinas <span className={optionalCls}>(opcional)</span></label>
                <textarea
                  value={vaccines}
                  onChange={(e) => setVaccines(e.target.value)}
                  className={inputCls}
                  placeholder="Ex: V10 em dia, antirrábica 2024..."
                  rows={2}
                />
              </div>

              {/* Description */}
              <div>
                <label className={labelCls}>Descrição <span className={optionalCls}>(opcional)</span></label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={inputCls}
                  placeholder="Observações adicionais..."
                  rows={2}
                />
              </div>

              {/* Unit. Fixed and read-only when the modal was opened from a unit's page,
                  searchable otherwise. */}
              {unitId ? (
                <div>
                  <label className={labelCls}>Unidade</label>
                  <div className={`px-3 py-2.5 text-sm rounded-lg border ${
                    lightTheme
                      ? 'bg-gray-50 border-gray-200 text-gray-700'
                      : 'bg-gray-800/60 border-gray-700 text-gray-300'
                  }`}>
                    {unitName || unitId}
                  </div>
                </div>
              ) : (
                <div>
                  <label className={labelCls}>Unidade <span className={optionalCls}>(opcional)</span></label>
                  <UnitSelector
                    customerId={customerId}
                    value={selectedUnit}
                    onChange={(opt) => setSelectedUnit(opt)}
                    condoType={(selectedCustomer as any)?.condo_data?.condo_type as any}
                    darkMode={!lightTheme}
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium border rounded-lg transition-colors ${
                    lightTheme
                      ? 'text-gray-600 bg-white hover:bg-gray-50 border-gray-300'
                      : 'text-gray-400 bg-transparent hover:bg-gray-800 border-gray-700'
                  }`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${
                    lightTheme
                      ? 'text-white bg-amber-600 hover:bg-amber-500'
                      : 'text-gray-900 bg-gray-100 hover:bg-white'
                  }`}
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Salvando...' : pet ? 'Salvar' : 'Cadastrar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <PhotoUploadModal
        open={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onConfirm={handlePhotoCropped}
        title={`Foto ${activeSlot} do Pet`}
        cropShape="rect"
        aspectRatio={4 / 5}
        darkMode={!lightTheme}
        disableFaceDetection={true}
      />
    </>
  );
};

export default PetFormModal;
